// ===== Silvify Variant Media Filter =====
/**
 * Filters product media by variant color. Only looks at metal codes: YG, WG, RG.
 * Ignores all other naming (RING, BRACELET, NECKLACE, EARRINGS, LBR-602, LBR, LR, sizes, etc).
 * 
 * Supported video patterns:
 * - BRACELET_YG, BRACELET_WG, BRACELET_RG
 * - RING_YG, RING_RG, RING_WG
 * - NECKLACE_YG, NECKLACE_WG, NECKLACE_RG
 * - EARRINGS_YG, EARRINGS_RG, EARRINGS_WG
 * 
 * Also supports any other naming pattern (e.g., LBR-602_YG) as long as it contains the metal code.
 * Images: any filename/URL containing YG/WG/RG.
 */
(function () {
  // Only these color codes matter; everything else in names is ignored
  const METAL_CODES = ['YG', 'RG', 'WG'];
  
  // Supported jewelry type prefixes (for documentation - actual filtering only checks metal codes)
  const JEWELRY_TYPES = ['BRACELET', 'RING', 'NECKLACE', 'EARRINGS'];

  /**
   * Get metal code from variant: YG, RG, WG from options/title (case-insensitive),
   * plus 9K/14K/18K/22K karat labels mapped to yellow / white / rose gold for media matching.
   */
  function getMetalCodeFromVariant(variant) {
    if (!variant) return 'WG';

    const parts = [
      variant.option1,
      variant.option2,
      variant.option3,
      variant.title
    ].filter(Boolean);
    const search = (parts.join(' ') || '').toLowerCase();

    // Only look for the three color codes; ignore everything else
    if (search.includes('yg')) return 'YG';
    if (search.includes('rg')) return 'RG';
    if (search.includes('wg')) return 'WG';

    // Karat gold (9K, 14K, 18K, 22K): same gallery codes as yellow / white / rose gold
    const hasKaratGold = /(^|[^a-z0-9])(9|14|18|22)\s*k([^a-z0-9]|$)/.test(search);
    if (hasKaratGold) {
      if (search.includes('rose')) return 'RG';
      if (search.includes('white')) return 'WG';
      return 'YG';
    }

    // Fallback: color words
    if (search.includes('yellow') || (search.includes('gold') && !search.includes('rose') && !search.includes('white'))) return 'YG';
    if (search.includes('rose')) return 'RG';
    if (search.includes('white') || search.includes('silver')) return 'WG';

    return 'WG';
  }

  /**
   * True if the string contains the metal code (YG, WG, RG). Only looks at colors; ignores rest.
   */
  function stringContainsMetalCode(str, code) {
    if (!str || !code) return false;
    return str.toLowerCase().includes(code.toLowerCase());
  }

  // Filter media by variant function (reusable for both variant change and page load)
  function filterMediaByVariant(variant) {
    if (!variant) return;

    const code = getMetalCodeFromVariant(variant);

    // Find the media gallery container first - only target elements within it
    const mediaGallery = document.querySelector('media-gallery') || 
                         document.querySelector('.product-information__media') ||
                         document.querySelector('[data-testid="product-information-media"]');
    
    if (!mediaGallery) return; // Exit if no media gallery found

    // Find all media items ONLY within the media gallery container
    // Use specific selectors to avoid matching product details section
    const mediaItems = mediaGallery.querySelectorAll(
      'slideshow-slide, ' +
      '.product-media-container, ' +
      '.media-gallery__grid li, ' +
      '.product-media-container--image, ' +
      '.product-media-container--video, ' +
      '.product-media-container--external_video, ' +
      '.product-media-container--model'
    );

    if (mediaItems.length === 0) return; // Exit if no media items found

    mediaItems.forEach(item => {
      // Check if this is a video container
      const isVideoContainer = item.classList.contains('product-media-container--video') ||
                               item.classList.contains('product-media-container--external_video') ||
                               item.querySelector('deferred-media') !== null;
      
      const deferredMedia = item.querySelector('deferred-media');
      const productMedia = item.querySelector('.product-media');
      const productMediaId = productMedia?.getAttribute('data-media-id');
      
      // Get the source URL - check data-media-src attribute first (added by Liquid template)
      // This contains the original filename for videos (e.g., RING_YG.mp4)
      // Try multiple methods to read the attribute
      let src = '';
      
      // Method 1: Direct getAttribute
      src = item.getAttribute('data-media-src');
      
      // Method 2: dataset property
      if (!src && item.dataset) {
        src = item.dataset.mediaSrc || item.dataset['media-src'];
      }
      
      // Method 3: attributes collection
      if (!src && item.attributes) {
        const attr = item.attributes.getNamedItem('data-media-src');
        if (attr) src = attr.value;
      }
      
      // Method 4: Check child elements (in case attribute is on a child)
      if (!src) {
        const childWithAttr = item.querySelector('[data-media-src]');
        if (childWithAttr) {
          src = childWithAttr.getAttribute('data-media-src');
        }
      }
      
      // Method 5: For videos, also check the product-media element inside
      if (!src && isVideoContainer && productMedia) {
        src = productMedia.getAttribute('data-media-src') || '';
      }
      
      // If no data attribute, fall back to checking DOM elements
      if (!src) {
      
      // 1. For videos, check the video element source
      if (isVideoContainer || deferredMedia) {
        // Check template content for video (before it's loaded)
        if (deferredMedia) {
          const template = deferredMedia.querySelector('template');
          if (template && template.content) {
            // Access template content (it's a DocumentFragment)
            const templateSource = template.content.querySelector('source');
            if (templateSource) {
              src = templateSource.getAttribute('src') || '';
            }
            
            // Also check video element in template
            if (!src) {
              const templateVideo = template.content.querySelector('video');
              if (templateVideo) {
                src = templateVideo.getAttribute('src') || '';
              }
            }
            
            // Also check for iframe in template (external videos)
            if (!src) {
              const templateIframe = template.content.querySelector('iframe');
              if (templateIframe) {
                src = templateIframe.getAttribute('src') || '';
              }
            }
          }
        }
        
        // Check if video is already loaded (not in template anymore)
        if (!src && deferredMedia) {
          const loadedVideo = deferredMedia.querySelector('video:not(template video)');
          if (loadedVideo) {
            src = loadedVideo.currentSrc || loadedVideo.src || loadedVideo.getAttribute('src') || '';
          }
          
          if (!src) {
            const loadedSource = loadedVideo?.querySelector('source');
            if (loadedSource) {
              src = loadedSource.getAttribute('src') || '';
            }
          }
          
          const loadedIframe = deferredMedia.querySelector('iframe:not(template iframe)');
          if (!src && loadedIframe) {
            src = loadedIframe.getAttribute('src') || '';
          }
        }
      }
      
      // 2. Check poster/preview image (for videos, this might also have the pattern)
      const posterImage = deferredMedia?.querySelector('.deferred-media__poster-image') ||
                         deferredMedia?.querySelector('img') ||
                         item.querySelector('.deferred-media__poster-image');
      
      if (posterImage && !src) {
        src = posterImage.currentSrc || 
              posterImage.src || 
              posterImage.getAttribute('src') || 
              posterImage.getAttribute('data-src') ||
              posterImage.getAttribute('data-max-resolution') ||
              '';
        
        // Check srcset
        if (!src) {
          const srcset = posterImage.getAttribute('srcset');
          if (srcset) {
            const firstSrc = srcset.split(',')[0].trim().split(' ')[0];
            if (firstSrc) src = firstSrc;
          }
        }
      }
      
      // 3. Check regular images (for image media types)
      if (!src) {
        const regularImage = item.querySelector('img:not(.deferred-media__poster-image)') ||
                            productMedia?.querySelector('img');
        
        if (regularImage) {
          src = regularImage.currentSrc || 
                regularImage.src || 
                regularImage.getAttribute('src') || 
                regularImage.getAttribute('data-src') ||
                regularImage.getAttribute('data-max-resolution') ||
                '';
          
          // Check srcset
          if (!src) {
            const srcset = regularImage.getAttribute('srcset');
            if (srcset) {
              const firstSrc = srcset.split(',')[0].trim().split(' ')[0];
              if (firstSrc) src = firstSrc;
            }
          }
        }
      }
      
      // 4. Try to get URL from data-media-id by finding the product-media element
      if (!src && productMediaId) {
        const mediaElement = mediaGallery.querySelector(`[data-media-id="${productMediaId}"]`);
        if (mediaElement) {
          // Check for image in product-media
          const mediaImg = mediaElement.querySelector('img');
          if (mediaImg) {
            src = mediaImg.currentSrc || mediaImg.src || mediaImg.getAttribute('src') || '';
          }
          
          // For videos, also check if there's a video element in product-media
          if (!src) {
            const mediaVideo = mediaElement.querySelector('video');
            if (mediaVideo) {
              src = mediaVideo.currentSrc || mediaVideo.src || mediaVideo.getAttribute('src') || '';
            }
          }
        }
      }
      
      // 5. For videos, check ALL possible image sources (preview images might have pattern in URL path)
      if (!src && isVideoContainer) {
        // Check all images in the item (poster, preview, etc.)
        const allImages = item.querySelectorAll('img');
        for (const img of allImages) {
          const imgSrc = img.currentSrc || 
                        img.src || 
                        img.getAttribute('src') || 
                        img.getAttribute('data-src') ||
                        img.getAttribute('data-max-resolution') ||
                        '';
          if (imgSrc) {
            src = imgSrc;
            break; // Use first image found
          }
          
          // Also check srcset
          if (!src) {
            const srcset = img.getAttribute('srcset');
            if (srcset) {
              const firstSrc = srcset.split(',')[0].trim().split(' ')[0];
              if (firstSrc) {
                src = firstSrc;
                break;
              }
            }
          }
        }
      }

      // For videos, if we still can't find a source, try one more thing:
      // Check if the preview image URL path might contain the pattern
      // (Shopify CDN URLs sometimes preserve the original filename in the path)
      if (!src && isVideoContainer && posterImage) {
        const posterSrc = posterImage.currentSrc || 
                         posterImage.src || 
                         posterImage.getAttribute('src') || '';
        if (posterSrc) {
          src = posterSrc;
        }
      }
      }

      // Collect every possible "name" string for this media (URL, filename, alt) – only match on YG/WG/RG
      const nameStrings = [];
      if (src) nameStrings.push(src);
      const dataMediaSrc = item.getAttribute('data-media-src') || item.dataset?.mediaSrc || (item.querySelector('[data-media-src]')?.getAttribute('data-media-src'));
      if (dataMediaSrc) nameStrings.push(dataMediaSrc);
      const altText = item.getAttribute('data-media-alt') || item.dataset?.mediaAlt || (item.attributes && item.attributes.getNamedItem('data-media-alt')?.value) || '';
      if (altText) nameStrings.push(altText);
      const posterImage = deferredMedia?.querySelector('.deferred-media__poster-image') || deferredMedia?.querySelector('img') || item.querySelector('.deferred-media__poster-image');
      if (posterImage) {
        const posterSrc = posterImage.currentSrc || posterImage.src || posterImage.getAttribute('src') || posterImage.getAttribute('data-src') || '';
        if (posterSrc) nameStrings.push(posterSrc);
      }

      if (isVideoContainer && deferredMedia) {
        const template = deferredMedia.querySelector('template');
        if (template && template.content) {
          const templateSource = template.content.querySelector('source');
          if (templateSource) nameStrings.push(templateSource.getAttribute('src') || '');
          const templateVideo = template.content.querySelector('video');
          if (templateVideo) nameStrings.push(templateVideo.getAttribute('src') || '');
        }
        const loadedVideo = deferredMedia.querySelector('video:not(template video)');
        if (loadedVideo) {
          nameStrings.push(loadedVideo.currentSrc || loadedVideo.src || loadedVideo.getAttribute('src') || '');
          const loadedSource = loadedVideo.querySelector('source');
          if (loadedSource) nameStrings.push(loadedSource.getAttribute('src') || '');
        }
      }

      // If we have no name strings at all, don't hide videos (show as fallback); hide images
      if (nameStrings.length === 0 || nameStrings.every(s => !s)) {
        if (isVideoContainer) {
          item.style.display = '';
          item.removeAttribute('hidden');
          return;
        }
        item.style.display = 'none';
        item.setAttribute('hidden', '');
        return;
      }

      // // Show if any of the names contain the metal code (YG, WG, RG) – ignore everything else
      // // This works for all jewelry types: BRACELET_YG, RING_YG, NECKLACE_YG, EARRINGS_YG, etc.
      // // The pattern matching is flexible - any filename containing the metal code will match
      // const urlContainsPattern = nameStrings.some(str => stringContainsMetalCode(str, code));
      
      // if (urlContainsPattern) {
      //   item.style.display = '';
      //   item.removeAttribute('hidden');
      // } else {
      //   item.style.display = 'none';
      //   item.setAttribute('hidden', '');
      // }
      // Check if this media has ANY metal code in its name at all
      const hasAnyMetalCode = METAL_CODES.some(c => nameStrings.some(str => stringContainsMetalCode(str, c)));

      // If no metal code is present, this is a dimension/generic image — show for all variants
      // Otherwise, only show if the name contains the currently selected metal code
      const shouldShow = !hasAnyMetalCode || nameStrings.some(str => stringContainsMetalCode(str, code));

      if (shouldShow) {
        item.style.display = '';
        item.removeAttribute('hidden');
      } else {
        item.style.display = 'none';
        item.setAttribute('hidden', '');
      }

    });

    // Hide dots for hidden media items and activate first visible slide
    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (slideshow) {
      const dots = slideshow.querySelectorAll('slideshow-controls button[ref="dots[]"]');
      const slides = slideshow.refs?.slides || slideshow.querySelectorAll('slideshow-slide');
      
      let firstVisibleSlideIndex = -1;
      
      // Hide dots that correspond to hidden slides and find first visible slide
      slides.forEach((slide, index) => {
        const slideItem = slide.querySelector('.product-media-container') || 
                         slide.querySelector('li[class*="product-media-container"]') ||
                         slide;
        const isHidden = slideItem.style.display === 'none' || slideItem.hasAttribute('hidden');
        
        // Find the corresponding dot (dots are 0-indexed, matching slide indices)
        if (dots[index]) {
          const dotParent = dots[index].closest('li');
          if (isHidden) {
            if (dotParent) {
              dotParent.style.display = 'none';
              dotParent.setAttribute('hidden', '');
            } else {
              dots[index].style.display = 'none';
              dots[index].setAttribute('hidden', '');
            }
            // Remove aria-selected from hidden dots
            dots[index].setAttribute('aria-selected', 'false');
          } else {
            if (dotParent) {
              dotParent.style.display = '';
              dotParent.removeAttribute('hidden');
            } else {
              dots[index].style.display = '';
              dots[index].removeAttribute('hidden');
            }
            
            // Track first visible slide index
            if (firstVisibleSlideIndex === -1) {
              firstVisibleSlideIndex = index;
            }
          }
        }
      });
      
      // Activate first visible slide and update dot indicators
      if (firstVisibleSlideIndex !== -1) {
        // First, clear all aria-selected attributes
        dots.forEach((dot, index) => {
          if (dot.style.display !== 'none' && !dot.hasAttribute('hidden')) {
            dot.setAttribute('aria-selected', 'false');
          }
        });
        
        // Set aria-selected on the first visible dot
        if (dots[firstVisibleSlideIndex]) {
          dots[firstVisibleSlideIndex].setAttribute('aria-selected', 'true');
        }
        
        // Activate the slide in the slideshow
        if (slideshow.select) {
          slideshow.select(firstVisibleSlideIndex, undefined, { animate: false });
        }
        
        // Also manually set the slideshow's current property to ensure it's in sync
        if (slideshow.current !== undefined) {
          slideshow.current = firstVisibleSlideIndex;
        }
      }
    } else {
      // Fallback: if no slideshow, try to click first visible media
      const firstVisible = [...mediaItems].find(el => {
        return el.style.display !== 'none' && !el.hasAttribute('hidden');
      });
      
      if (firstVisible && firstVisible.click) {
        firstVisible.click();
      }
    }
  }

  // Listen to variant:update event (this theme's standard variant change event)
  document.addEventListener('variant:update', function (event) {
    // This theme's event structure uses event.detail?.resource
    filterMediaByVariant(event.detail?.resource);
  });

  // Run on initial page load
  function initFilter() {
    // Find product form to get selected variant
    const productForm = document.querySelector('product-form-component, form[action*="/cart/add"]');
    if (!productForm) return;

    // Get selected variant ID from form
    const variantInput = productForm.querySelector('[name="id"]');
    if (!variantInput) return;

    const selectedId = variantInput.value;
    if (!selectedId) return;

    // Try to find variant data from variant-picker component (this theme stores it there)
    let variant = null;
    
    // Check variant-picker script tag first (this theme's structure)
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const variantScript = variantPicker.querySelector('script[type="application/json"]');
      if (variantScript) {
        try {
          variant = JSON.parse(variantScript.textContent);
          // If this is the selected variant, use it
          if (String(variant?.id) === String(selectedId)) {
            filterMediaByVariant(variant);
            return;
          }
        } catch (e) {
          // Continue to other methods
        }
      }
    }

    // Try to find product variants data from global product object
    if (!window.productVariants) {
      // Check for product JSON in script tag
      const productJson = document.querySelector('script[type="application/json"][data-product], script[type="application/json"]#ProductJson-product-template');
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          window.productVariants = productData.variants || productData.product?.variants;
        } catch (e) {
          // Continue to other methods
        }
      }
    }

    // Find the selected variant from variants array
    if (window.productVariants) {
      variant = window.productVariants.find(v => String(v.id) === String(selectedId));
    }

    if (variant) {
      filterMediaByVariant(variant);
    }
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilter);
  } else {
    // DOM already loaded, run immediately
    initFilter();
  }

})();
// ===== End Silvify Variant Media Filter =====
